/**
 * FuarListPage.jsx — Fuar Listesi / fuar seçimi
 * Route: /Management/Fairs
 *
 * CANLI: `GET /fuar/list?languageId=1` (düz dizi — backend sayfalamıyor, arama da yok;
 * ikisi de burada yapılıyor) + seçim için `PUT /users/updateUsersActiveFuar`
 * (FairContext.activateFair — üst menüdeki "Fuar Seç" ile aynı çağrı).
 *
 * SEÇİLİ FUAR — kullanıcının üzerinde çalıştığı TEK fuar (MainContext.selectedFuarId, backend'de
 * user.fuar_id). Fuar Kuralları, Stand Listesi vb. bununla kapsanıyor. Bu ekranda "Seçili Fuar"
 * şeridi (primary) YALNIZCA ona gidiyor.
 *
 * WAS: a second, unrelated "aktif" concept — fuar_report_active, a per-fair flag several fairs
 * could hold at once with no relation to the selected fair — showed as its own "Raporlarda aktif"
 * badge/filter/column here. Removed by backlog #42 (2026-09-18): the flag's own help text said as
 * much, live php-mysql has it 0 on every one of 86 fairs, and it was never actually used for
 * anything a report reads — see FuarService/FuarController for what's left of the column itself
 * (kept, since BetterStandsService still reads it; just no longer settable or filterable here).
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container, Card, CardBody, Button, Row, Col, Input,
  Pagination, PaginationItem, PaginationLink, Badge, Spinner, Alert,
  Dropdown, DropdownToggle, DropdownMenu, DropdownItem,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import MainContext from "../../../Components/Contexts/MainContext";
import FairContext from "../../../Components/Contexts/Management/FairContext";
import { fetchFuarList, archiveFuar, unarchiveFuar, clearError } from "../../../slices/fuarSlice";
import { apiErrorMessage, fuarService } from "../../../services/fuarService";
import KontrolluSilDugmesi from "../../../Components/Common/KontrolluSilDugmesi";
import FuarLogoImg from "../../../Components/Fairs/FuarLogoImg";
import FuarBilgisiModal from "../../../Components/Fairs/FuarBilgisiModal";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

const PAGE_SIZES = [12, 24, 48, 96];

// Backlog #16/#19, reworked by #45 (backend)/#46 (this dropdown). Yıl etiketleri tarayıcının
// saatinden hesaplanır, hiçbir yerde sabit yazılmaz — her yıl kendiliğinden ilerler.
const CURRENT_YEAR = new Date().getFullYear();
const PREVIOUS_YEAR = CURRENT_YEAR - 1;
// #45's rolling 3-year band, right after PREVIOUS_YEAR — e.g. "2024-2022" when CURRENT_YEAR is 2026.
const PAST_BAND_LABEL = `${CURRENT_YEAR - 2}-${CURRENT_YEAR - 4}`;

// WAS: a flat Nav-pills bar, one tab per group, "Geçmiş" itself a single open-ended bucket.
// const GROUP_TABS = [
//   { key: "ALL", label: "Tümü" },
//   { key: "FUTURE", label: "Gelecek" },
//   { key: "ACTIVE", label: "Aktif" },
//   { key: "CURRENT_YEAR", label: String(CURRENT_YEAR) },
//   { key: "PREVIOUS_YEAR", label: String(PREVIOUS_YEAR) },
//   { key: "PAST", label: "Geçmiş" },
//   { key: "ARCHIVED", label: "Arşiv" },
// ];
// Now: one dropdown (Deniz, 2026-09-17). Aktif/Gelecek/Tümü are direct choices; Geçmiş is a header
// over its four sub-items rather than a selectable group of its own — reactstrap has no native
// submenu, so this is a plain DropdownItem "header" followed by indented items (the simpler of the
// two options the task offered, over a nested direction="end" Dropdown).
const TOP_LEVEL_OPTIONS = [
  { key: "ACTIVE", get label() { return i18n.t("yonetim:fuarList.aktif"); } },
  { key: "FUTURE", get label() { return i18n.t("yonetim:fuarList.gelecek"); } },
];
const GECMIS_OPTIONS = [
  { key: "CURRENT_YEAR", label: String(CURRENT_YEAR) },
  { key: "PREVIOUS_YEAR", label: String(PREVIOUS_YEAR) },
  { key: "PAST_3Y", label: PAST_BAND_LABEL },
  { key: "ARCHIVED", get label() { return i18n.t("yonetim:fuarList.arsiv"); } },
];
const ALL_OPTION = { key: "ALL", get label() { return i18n.t("common:tumu"); } };
const GROUP_LABEL = Object.fromEntries(
  [...TOP_LEVEL_OPTIONS, ...GECMIS_OPTIONS, ALL_OPTION].map((o) => [o.key, o.label])
);

// Kapanmış fuarlar arşivlenebilir; hâlâ süren (FUTURE/ACTIVE) bir fuarı arşivlemek anlamsız.
// WAS: new Set(["CURRENT_YEAR", "PREVIOUS_YEAR", "PAST"]) — PAST_3Y is #45's replacement for PAST.
const CLOSED_GROUPS = new Set(["CURRENT_YEAR", "PREVIOUS_YEAR", "PAST_3Y"]);

const normalize = (s) => (s || "").toLocaleLowerCase("tr-TR");

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString(currentLocaleTag(), { day: "numeric", month: "long", year: "numeric" })
    : "—";

const FuarListPage = () => {
  const { t: tx } = useTranslation("yonetim");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { list: fuarlar, loading, error } = useSelector((state) => state.fuar);
  const { selectedFuarId, setSelectedFuarId } = useContext(MainContext);
  const { activateFair } = useContext(FairContext);

  const [viewMode, setViewMode] = useState("grid");
  const [search, setSearch] = useState("");
  // Backlog #45/#46: default view is now Aktif, not Tümü — the old #16 behavior left fuars with no
  // usable end date (see FuarRepository.FUAR_CLOSES_EXPR) sitting in ACTIVE forever, which used to be
  // buried under "Tümü"; defaulting here surfaces that instead of hiding it.
  // Backlog #96 (2026-09-22): multiple bands at once, e.g. Aktif + 2025. Still client-side only —
  // the backend fetch below has never depended on this, it just narrows the already-fetched list.
  // "ALL" stays mutually exclusive with everything else (it already means "every band but Arşiv").
  const [groupTabs, setGroupTabs] = useState(["ACTIVE"]); // #16 fuarGroup — ALL arşiv HARİÇ hepsi demek
  const [groupMenuOpen, setGroupMenuOpen] = useState(false);
  const [pageSize, setPageSize] = useState(24);
  const [page, setPage] = useState(1);
  const [selectingId, setSelectingId] = useState(null);
  const [selectMsg, setSelectMsg] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const [detayFuar, setDetayFuar] = useState(null); // #561: the fair whose FuarBilgisiModal is open

  useEffect(() => { document.title = i18n.t("yonetim:fuarList.fuarlarFds"); }, []);

  useEffect(() => {
    dispatch(fetchFuarList()); // WAS (until #332): fetchFuarList({ languageId: 1 }) -- now the language on screen
  }, [dispatch]);

  useEffect(() => {
    if (!selectMsg) return undefined;
    const t = setTimeout(() => setSelectMsg(null), 4000);
    return () => clearTimeout(t);
  }, [selectMsg]);

  // Filtre/arama değişince ilk sayfaya dön — setState render sırasında değil, olay anında.
  const onSearch = (e) => { setSearch(e.target.value); setPage(1); };
  const onPageSize = (e) => { setPageSize(Number(e.target.value)); setPage(1); };
  const onGroupTab = (key) => {
    setPage(1);
    if (key === ALL_OPTION.key) {
      setGroupTabs([ALL_OPTION.key]);
      setGroupMenuOpen(false);
      return;
    }
    setGroupTabs((prev) => {
      const withoutAll = prev.filter((k) => k !== ALL_OPTION.key);
      if (withoutAll.includes(key)) {
        const next = withoutAll.filter((k) => k !== key);
        return next.length > 0 ? next : withoutAll; // at least one band stays selected
      }
      return [...withoutAll, key];
    });
    // Menu stays open so more bands can be picked; reactstrap's own outside-click still closes it.
  };

  // Seçenek başına sayaç — tam liste üzerinden, arama/rapor filtresinden bağımsız.
  // WAS: counts had a PAST key — #45 replaced the open-ended PAST bucket with PAST_3Y.
  const groupCounts = useMemo(() => {
    const counts = { ALL: 0, FUTURE: 0, ACTIVE: 0, CURRENT_YEAR: 0, PREVIOUS_YEAR: 0, PAST_3Y: 0, ARCHIVED: 0 };
    fuarlar.forEach((f) => {
      if (f.fuarGroup && counts[f.fuarGroup] !== undefined) counts[f.fuarGroup] += 1;
      if (f.fuarGroup !== "ARCHIVED") counts.ALL += 1;
    });
    return counts;
  }, [fuarlar]);

  const selectedFair = useMemo(
    () => fuarlar.find((f) => Number(f.fuarId) === Number(selectedFuarId)) || null,
    [fuarlar, selectedFuarId]
  );

  const filtered = useMemo(() => {
    const q = normalize(search.trim());
    return fuarlar
      .filter((f) => {
        // #16/#19: "Tümü" arşivlenmiş fuarları göstermez — Arşiv sekmesi ayrı, kapalı kutu.
        // #96: groupTabs is now a set of bands — a fair matches if it's in ANY of them.
        if (groupTabs.includes(ALL_OPTION.key)) { if (f.fuarGroup === "ARCHIVED") return false; }
        else if (!groupTabs.includes(f.fuarGroup)) return false;
        if (!q) return true;
        return [f.fuarName, f.fuarDescription, f.fuarAlaniName, String(f.fuarId)]
          .some((v) => normalize(v).includes(q));
      })
      // En yeni fuar en üstte — liste backend'den sırasız geliyor.
      .sort((a, b) => String(b.fuarStartDate || "").localeCompare(String(a.fuarStartDate || "")));
  }, [fuarlar, search, groupTabs]);

  // Toggle başlığı/rozeti: tek seçim eski etiketi gösterir, birden çoğu "N seçili" der.
  const groupTabLabel = groupTabs.includes(ALL_OPTION.key)
    ? ALL_OPTION.label
    : groupTabs.length === 1
      ? (GROUP_LABEL[groupTabs[0]] || groupTabs[0])
      : tx("fuarList.secili", { length: groupTabs.length });
  const groupTabCount = groupTabs.includes(ALL_OPTION.key)
    ? (groupCounts.ALL ?? 0)
    : groupTabs.reduce((sum, k) => sum + (groupCounts[k] ?? 0), 0);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  async function handleSelect(fuar, e) {
    e?.stopPropagation();
    const userId = localStorage.getItem("userId");
    setSelectingId(fuar.fuarId);
    try {
      await activateFair(userId, fuar.fuarId);
      setSelectedFuarId(fuar.fuarId);
      // Backlog #44: WAS also writing a second, independent `user.fuarId` copy to localStorage here
      // (and in Header.handleActivateFair) — nothing reads it any more now that the old /Fairs page
      // (its only reader) redirects to /Management/Fairs. activeFairId below is the one copy that
      // matters — read by MainContext.fetchActiveFairId's own fallback so a reload gets it back.
      localStorage.setItem("activeFairId", String(fuar.fuarId));
      setSelectMsg({ color: "success", text: tx("fuarList.seciliFuarYapildi", { fuarName: fuar.fuarName }) });
    } catch (err) {
      setSelectMsg({ color: "danger", text: tx("fuarList.fuarSecilemedi", { apiErrorMessage: apiErrorMessage(err) }) });
    } finally {
      setSelectingId(null);
    }
  }

  const goEdit = (fuar) => navigate(`/Management/Fairs/${fuar.fuarId}`);

  async function handleArchiveToggle(fuar, e) {
    e?.stopPropagation();
    const archiving = fuar.fuarGroup !== "ARCHIVED";
    setArchivingId(fuar.fuarId);
    try {
      await dispatch(archiving ? archiveFuar(fuar.fuarId) : unarchiveFuar(fuar.fuarId)).unwrap();
      setSelectMsg({
        color: "success",
        text: archiving ? tx("fuarList.arsivlendi", { fuarName: fuar.fuarName }) : tx("fuarList.arsivdenCikarildi", { fuarName: fuar.fuarName }),
      });
      // Arşivden çıkarınca hangi tarih grubuna düştüğünü yalnızca backend bilir (bkz. slice) —
      // arşivlemede satırı sliceʼın optimistic güncellemesi zaten hallediyor.
      if (!archiving) dispatch(fetchFuarList()); // WAS (until #332): fetchFuarList({ languageId: 1 }) -- now the language on screen
    } catch (err) {
      setSelectMsg({ color: "danger", text: tx("fuarList.islemBasarisiz", { apiErrorMessage: apiErrorMessage(err) }) });
    } finally {
      setArchivingId(null);
    }
  }

  const renderSelectControl = (fuar, { block = false } = {}) => {
    const isSelected = Number(fuar.fuarId) === Number(selectedFuarId);
    if (isSelected) {
      return (
        <Badge color="primary" className={`py-2 px-2 ${block ? "flex-grow-1" : ""}`}>
          <i className="bx bx-check-circle me-1" />{tx("fuarList.secili2")}
        </Badge>
      );
    }
    return (
      <Button
        size="sm" color="primary" outline className={block ? "flex-grow-1" : ""}
        disabled={selectingId != null} onClick={(e) => handleSelect(fuar, e)}
      >
        {selectingId === fuar.fuarId ? <Spinner size="sm" /> : <><i className="bx bx-pointer me-1" />{tx("fuarList.sec")}</>}
      </Button>
    );
  };

  const renderArchiveControl = (fuar) => {
    const isArchived = fuar.fuarGroup === "ARCHIVED";
    if (!isArchived && !CLOSED_GROUPS.has(fuar.fuarGroup)) return null; // hâlâ süren fuar — arşivlenemez
    return (
      <Button
        size="sm" color={isArchived ? "success" : "secondary"} outline
        disabled={archivingId != null}
        title={isArchived ? tx("fuarList.arsivdenCikar") : tx("fuarList.arsivle")}
        onClick={(e) => handleArchiveToggle(fuar, e)}
      >
        {archivingId === fuar.fuarId
          ? <Spinner size="sm" />
          : <i className={`bx ${isArchived ? "bx-upload" : "bx-archive-in"}`} />}
      </Button>
    );
  };

  // #550: only an EMPTY fair can be deleted. The check runs when the button is clicked (a page of fairs is not a page of requests); the fair
  // selected in the top bar is refused up front — the user's own selection would point at a deleted fair.
  const renderDeleteControl = (fuar) => (
    <KontrolluSilDugmesi
      key={fuar.fuarId} tur="fuar" ad={fuar.fuarName} simgeSadece tikla
      engel={Number(fuar.fuarId) === Number(selectedFuarId) ? tx("components:kaliciSil.seciliFuar") : null}
      kontrol={() => fuarService.getFuarSilmeKontrolu(fuar.fuarId)}
      sil={() => fuarService.deleteFuar(fuar.fuarId)}
      onSilindi={() => {
        setSelectMsg({ color: "success", text: tx("fuarList.silindi", { fuarName: fuar.fuarName }) });
        dispatch(fetchFuarList());
      }}
    />
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) pages.push(i);
      else if (pages[pages.length - 1] !== "…") pages.push("…");
    }
    return (
      <div className="d-flex justify-content-center mt-3">
        <Pagination>
          <PaginationItem disabled={currentPage === 1}>
            <PaginationLink previous onClick={() => setPage(currentPage - 1)} />
          </PaginationItem>
          {pages.map((p, idx) => (
            <PaginationItem key={`${p}-${idx}`} active={p === currentPage} disabled={p === "…"}>
              <PaginationLink onClick={() => typeof p === "number" && setPage(p)}>{p}</PaginationLink>
            </PaginationItem>
          ))}
          <PaginationItem disabled={currentPage === totalPages}>
            <PaginationLink next onClick={() => setPage(currentPage + 1)} />
          </PaginationItem>
        </Pagination>
      </div>
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("fuarList.fuarListesi")} pageTitle={tx("fuarList.fuarlar")} />

        {error && (
          <Alert color="danger" toggle={() => dispatch(clearError())}>{error}</Alert>
        )}
        {selectMsg && <Alert color={selectMsg.color}>{selectMsg.text}</Alert>}

        {/* Seçili fuar — ekranın asıl sorusu "şu an hangi fuardayım" */}
        <Card className="mb-3 border border-primary border-opacity-25">
          <CardBody className="py-3 d-flex align-items-center gap-3 flex-wrap">
            <div className="avatar-sm flex-shrink-0">
              <span className="avatar-title rounded bg-primary-subtle text-primary fs-4">
                <i className="bx bx-calendar-check" />
              </span>
            </div>
            <div className="flex-grow-1">
              <div className="text-muted small text-uppercase">{tx("fuarList.seciliFuar")}</div>
              {selectedFair ? (
                <>
                  <h5 className="mb-0">{selectedFair.fuarName}</h5>
                  <div className="text-muted small">
                    {formatDate(selectedFair.fuarStartDate)} – {formatDate(selectedFair.fuarEndDate)}
                    {selectedFair.fuarAlaniName && <> · {selectedFair.fuarAlaniName}</>}
                  </div>
                </>
              ) : (
                <h5 className="mb-0 text-muted">
                  {selectedFuarId ? tx("fuarList.fuar", { selectedFuarId }) : tx("fuarList.seciliFuarYok")}
                </h5>
              )}
            </div>
            {selectedFair && (
              <div className="d-flex gap-2">
                <Button size="sm" color="primary" outline onClick={() => setDetayFuar(selectedFair)}>
                  <i className="bx bx-info-circle me-1" />{tx("fuarBilgisi.baslik")}
                </Button>
                <Button size="sm" color="primary" outline onClick={() => goEdit(selectedFair)}>
                  <i className="bx bx-edit me-1" />{tx("common:duzenle")}
                </Button>
                <Button size="sm" color="primary" onClick={() => navigate("/Management/Fairs/Rules")}>
                  <i className="bx bx-shield-quarter me-1" />{tx("fuarList.fuarKurallari")}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Araç çubuğu */}
        {/* WAS: a separate Nav-pills bar above this card, one tab per group — see the GROUP_TABS
            comment above. Backlog #46 (Deniz, 2026-09-17) replaces it with one dropdown, placed
            before the view-mode/Seriler/Yeni Fuar buttons it now shares a row with. */}
        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-center">
              <Col lg={5} md={6}>
                <div className="search-box">
                  <Input
                    type="text" className="form-control search"
                    placeholder={tx("fuarList.fuarAdiFuarAlaniVeya")}
                    value={search} onChange={onSearch}
                  />
                  <i className="ri-search-line search-icon" />
                </div>
              </Col>
              {/* Backlog #42: WAS a Col lg={2} md={3} "Rapor durumu" select here (Tüm fuarlar /
                  Raporlarda aktif / Pasif) filtering on fuarReportActive — removed along with the
                  flag's other UI (fairsform.js switch, the grid badge and list column below). */}
              <Col lg={3} md={3} xs={6}>
                <Input type="select" value={pageSize} onChange={onPageSize} aria-label={tx("fuarList.sayfaBoyutu")}>
                  {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}{" "}{tx("fuarList.goster")}</option>)}
                </Input>
              </Col>
              <Col lg={4} className="text-lg-end d-flex gap-2 justify-content-lg-end flex-wrap">
                <Dropdown isOpen={groupMenuOpen} toggle={() => setGroupMenuOpen((o) => !o)}>
                  <DropdownToggle caret color="primary" outline>
                    <i className="bx bx-filter-alt me-1" />
                    {groupTabLabel}
                    <Badge color="primary-subtle" className="text-primary ms-2">
                      {groupTabCount}
                    </Badge>
                  </DropdownToggle>
                  {/* #96: birden çok bant seçilebilir — Tümü hariç, tıklama menüyü kapatmaz. */}
                  <DropdownMenu end>
                    {TOP_LEVEL_OPTIONS.map((opt) => (
                      <DropdownItem
                        key={opt.key} active={groupTabs.includes(opt.key)} toggle={false}
                        onClick={() => onGroupTab(opt.key)}
                      >
                        <i className={`bx ${groupTabs.includes(opt.key) ? "bx-check-square" : "bx-square"} me-1`} />
                        {opt.label}
                        <Badge color="light" className="text-muted ms-2">{groupCounts[opt.key] ?? 0}</Badge>
                      </DropdownItem>
                    ))}
                    <DropdownItem header>{tx("fuarList.gecmis")}</DropdownItem>
                    {GECMIS_OPTIONS.map((opt) => (
                      <DropdownItem
                        key={opt.key} active={groupTabs.includes(opt.key)} toggle={false}
                        onClick={() => onGroupTab(opt.key)}
                        className="ps-4"
                      >
                        <i className={`bx ${groupTabs.includes(opt.key) ? "bx-check-square" : "bx-square"} me-1`} />
                        {opt.label}
                        <Badge color="light" className="text-muted ms-2">{groupCounts[opt.key] ?? 0}</Badge>
                      </DropdownItem>
                    ))}
                    <DropdownItem divider />
                    <DropdownItem active={groupTabs.includes(ALL_OPTION.key)} onClick={() => onGroupTab(ALL_OPTION.key)}>
                      {ALL_OPTION.label}
                      <Badge color="light" className="text-muted ms-2">{groupCounts[ALL_OPTION.key] ?? 0}</Badge>
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button color="secondary" outline onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}>
                  <i className={`bx ${viewMode === "grid" ? "bx-list-ul" : "bx-grid-alt"} me-1`} />
                  {viewMode === "grid" ? tx("fuarList.liste") : tx("fuarList.kart")}
                </Button>
                <Button color="primary" outline onClick={() => navigate("/Management/Fairs/Series")}>
                  <i className="bx bx-collection me-1" />{tx("fuarList.seriler")}
                </Button>
                <Button color="primary" onClick={() => navigate("/Management/Fairs/new")}>
                  <i className="bx bx-plus me-1" />{tx("fuarList.yeniFuar")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {loading && (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">{tx("common:yukleniyor")}</p>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <Card><CardBody className="text-center text-muted py-5">{tx("fuarList.fuarBulunamadi")}</CardBody></Card>
        )}

        {/* Kart görünümü */}
        {!loading && viewMode === "grid" && pageRows.length > 0 && (
          <Row>
            {pageRows.map((fuar) => {
              const isSelected = Number(fuar.fuarId) === Number(selectedFuarId);
              return (
                <Col xl={3} lg={4} md={6} className="mb-4" key={fuar.fuarId}>
                  <Card className={`h-100 overflow-hidden ribbon-box ${isSelected ? "border border-primary" : ""}`}>
                    {isSelected && (
                      <div className="ribbon ribbon-primary round-shape" style={{ zIndex: 1 }}>{tx("fuarList.seciliFuar")}</div>
                    )}
                    <div
                      className="d-flex align-items-center justify-content-center bg-light p-3 border-bottom"
                      style={{ height: 130, cursor: "pointer" }}
                      onClick={() => goEdit(fuar)}
                    >
                      <FuarLogoImg
                        url={fuar.fuarLogoUrl} fileName={fuar.fuarLogo} alt={fuar.fuarName}
                        style={{ maxHeight: 96, maxWidth: "100%", objectFit: "contain" }}
                      />
                    </div>
                    <CardBody className="d-flex flex-column">
                      <h6 className="mb-2" style={{ minHeight: 38 }}>
                        <Button color="link" className="p-0 text-start text-body fw-semibold" onClick={() => goEdit(fuar)}>
                          {fuar.fuarName}
                        </Button>
                      </h6>
                      <p className="text-muted small mb-1">
                        <i className="bx bx-calendar me-1" />
                        {formatDate(fuar.fuarStartDate)} – {formatDate(fuar.fuarEndDate)}
                      </p>
                      <p className="text-muted small mb-2">
                        <i className="bx bx-map me-1" />{fuar.fuarAlaniName || "—"}
                      </p>
                      <div className="mb-3 d-flex gap-1 flex-wrap">
                        <Badge color="light" className="text-dark border">#{fuar.fuarId}</Badge>
                      </div>
                      <div className="mt-auto d-flex gap-2">
                        {renderSelectControl(fuar, { block: true })}
                        <Button size="sm" color="primary" outline onClick={() => setDetayFuar(fuar)} title={tx("fuarBilgisi.baslik")}>
                          <i className="bx bx-info-circle" />
                        </Button>
                        <Button size="sm" color="primary" outline onClick={() => goEdit(fuar)} title={tx("fuarList.detayDuzenle")}>
                          <i className="bx bx-edit" />
                        </Button>
                        {renderArchiveControl(fuar)}
                        {renderDeleteControl(fuar)}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Liste görünümü */}
        {!loading && viewMode === "list" && pageRows.length > 0 && (
          <Card>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 70 }}></th>
                    <th>{tx("fuarList.fuar2")}</th>
                    <th>{tx("fuarList.tarih")}</th>
                    <th>{tx("fuarList.fuarAlani")}</th>
                    <th className="text-end" style={{ width: 250 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((fuar) => {
                    const isSelected = Number(fuar.fuarId) === Number(selectedFuarId);
                    return (
                      <tr
                        key={fuar.fuarId} style={{ cursor: "pointer" }}
                        className={isSelected ? "table-active" : ""} onClick={() => goEdit(fuar)}
                      >
                        <td>
                          <FuarLogoImg
                            url={fuar.fuarLogoUrl} fileName={fuar.fuarLogo}
                            style={{ maxHeight: 40, maxWidth: 56, objectFit: "contain" }}
                          />
                        </td>
                        <td>
                          <div className="fw-semibold">{fuar.fuarName}</div>
                          <div className="text-muted small">#{fuar.fuarId}</div>
                        </td>
                        <td className="small">{formatDate(fuar.fuarStartDate)} – {formatDate(fuar.fuarEndDate)}</td>
                        <td className="small">{fuar.fuarAlaniName || "—"}</td>
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            {renderSelectControl(fuar)}
                            <Button size="sm" color="primary" outline onClick={(e) => { e.stopPropagation(); setDetayFuar(fuar); }} title={tx("fuarBilgisi.baslik")}>
                              <i className="bx bx-info-circle" />
                            </Button>
                            <Button size="sm" color="primary" outline onClick={(e) => { e.stopPropagation(); goEdit(fuar); }}>
                              <i className="bx bx-edit" />
                            </Button>
                            {renderArchiveControl(fuar)}
                            {renderDeleteControl(fuar)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {!loading && renderPagination()}

        <FuarBilgisiModal fuar={detayFuar} isOpen={Boolean(detayFuar)} toggle={() => setDetayFuar(null)} />

        {!loading && filtered.length > 0 && (
          <div className="text-center text-muted small mt-2">
            {tx("fuarList.fuar3", { length: filtered.length })}{filtered.length !== fuarlar.length && tx("fuarList.toplam", { length: fuarlar.length })}{" "}{tx("fuarList.sayfa", { currentPage, totalPages })}
          </div>
        )}
      </Container>
    </div>
  );
};

export default FuarListPage;
